import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState, StatusBadge, fmtDate } from "@/components/shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Dot } from "recharts";
import { BarChart3, ArrowRight, TrendingUp } from "lucide-react";

function bandColor(band) {
  return {
    "High Maturity": "text-emerald-600",
    "Developing": "text-indigo-600",
    "Emerging": "text-amber-600",
    "Foundational": "text-rose-600",
  }[band] || "text-foreground";
}

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => { api.get("/reports").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return <Loader label="Loading reports" />;
  const isTac = user.role === "tac";

  return (
    <div>
      <PageHeader
        overline="Scoring & reporting"
        title="Reports"
        description={isTac ? "Your submission summaries. Full breakdowns are visible to EnterpriseSG reviewers." : "Full score sets across all partner submissions, tied to specific versions."}
      />
      {items.length >= 2 && <ScoreTrend items={items} isTac={isTac} />}
      {items.length === 0 ? (
        <EmptyState icon={BarChart3} title="No reports yet" description="Reports appear once assessments are submitted." />
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {!isTac && <TableHead className="text-xs uppercase tracking-wider">Partner</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider">Title</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Version</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Overall</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Band</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Submitted</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r, i) => (
                <motion.tr key={r.submission_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                  className="border-b border-border/60 hover:bg-muted/40 transition-colors cursor-pointer"
                  onClick={() => navigate(`/reports/${r.submission_id}`)} data-testid={`report-row-${r.submission_id}`}>
                  {!isTac && <TableCell className="py-3 text-sm font-medium">{r.tac_org || r.tac_name}</TableCell>}
                  <TableCell className="py-3">{r.title}</TableCell>
                  <TableCell className="py-3 font-mono text-sm">v{r.version}</TableCell>
                  <TableCell className="py-3 font-heading font-bold">{r.scores?.overall ?? "—"}</TableCell>
                  <TableCell className={`py-3 text-sm font-semibold ${bandColor(r.scores?.band)}`}>{r.scores?.band}</TableCell>
                  <TableCell className="py-3 text-sm text-muted-foreground font-mono">{fmtDate(r.submitted_at)}</TableCell>
                  <TableCell className="py-3 text-right pr-4"><ArrowRight className="h-4 w-4 text-muted-foreground inline" aria-hidden="true" /></TableCell>
                </motion.tr>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export { bandColor };

function ScoreTrend({ items, isTac }) {
  // Build one line per partner (org), points ordered by submission time.
  const byOrg = {};
  items.slice().sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at)).forEach((r) => {
    const org = r.tac_org || r.tac_name || "Partner";
    (byOrg[org] = byOrg[org] || []).push({ label: `v${r.version}`, overall: r.scores?.overall ?? null, org });
  });
  const orgs = Object.keys(byOrg);
  // For reviewers with many partners, focus on the partner with the most versions.
  const focusOrg = orgs.sort((a, b) => byOrg[b].length - byOrg[a].length)[0];
  const series = byOrg[focusOrg] || [];
  if (series.length < 2) return null;

  return (
    <div className="mb-6 bg-card border border-border rounded-md p-6" data-testid="score-trend">
      <div className="flex items-center justify-between mb-4">
        <div className="overline flex items-center gap-2"><TrendingUp className="h-3.5 w-3.5" aria-hidden="true" /> Overall score trend {!isTac && <span className="normal-case tracking-normal text-muted-foreground">· {focusOrg}</span>}</div>
        <div className="text-xs text-muted-foreground font-mono">{series.length} versions</div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={series} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#667085" }} axisLine={{ stroke: "#D9DEE5" }} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#667085" }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #D9DEE5", fontSize: 12 }} formatter={(v) => [`${v} / 100`, "Overall"]} />
          <Line type="monotone" dataKey="overall" stroke="#F33540" strokeWidth={2.5} dot={{ r: 4, fill: "#303E49" }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
